import React from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";
import MDAvatar from "materialDashboard/components/MDAvatar";

// Images
import backgroundImage from "assets/images/bg-profile.jpeg";
import {useTranslation} from "react-i18next";
import Icon from "@mui/material/Icon";
import EditRoleDialog from "../EditRoleDialog";
import {useCurrentUser} from "../../../auth/CurrentUserProvider";
import MDDeleteButton from "../../../components/MDDeleteButton";
import {useUserDeleteMutation} from "../query";
import useResourceAuthorization from "../../auth/useResourceAuthorization";


function Header(props) {
    const {t} = useTranslation(['enums', 'users']);
    const {currentUser} = useCurrentUser();
    const role = currentUser?.data?.role ?? null;

    const {children, user, tabsEl} = props;
    const {name, surname} = user;
    const initials = name?.length > 0 && surname?.length > 0 ? name.charAt(0) + surname.charAt(0) : "U";

    const [editRoleDialogOpen, setEditRoleDialogOpen] = React.useState(false);
    const handleEditRoleDialogOpen = () => setEditRoleDialogOpen(true);
    const handleEditRoleDialogClose = () => setEditRoleDialogOpen(false);

    const deleteUser = useUserDeleteMutation(user.id);
    const authorization = useResourceAuthorization(user);

    const handleDelete = async () => {
        await deleteUser.mutateAsync();
    }

    return (
        <MDBox position="relative" mb={5}>
            <MDBox
                display="flex"
                alignItems="center"
                position="relative"
                minHeight="18.75rem"
                borderRadius="xl"
                sx={{
                    backgroundImage: ({functions: {rgba, linearGradient}, palette: {gradients}}) =>
                        `${linearGradient(
                            rgba(gradients.info.main, 0.6),
                            rgba(gradients.info.state, 0.6)
                        )}, url(${backgroundImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "50%",
                    overflow: "hidden",
                }}
            />
            <Card
                sx={{
                    position: "relative",
                    mt: -8,
                    mx: 3,
                    py: 2,
                    px: 2,
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid item>
                        <MDAvatar src={""} alt="profile-image" size="xl" shadow="sm" bgColor={"dark"}>
                            {initials}
                        </MDAvatar>
                    </Grid>
                    <Grid item>
                        <MDBox height="100%" mt={0.5} lineHeight={1}>
                            <MDTypography variant="h5" fontWeight="medium">
                                {user?.surname || ""} {user?.name || ""}
                            </MDTypography>
                            <MDTypography variant="button" color="text" fontWeight="regular">
                                {user?.role ? t(`role.${user?.role}`) : ""}
                                {
                                    (role === "ADMIN" || role === "WG_ADMIN") &&
                                    <IconButton sx={{fontSize: "1rem", ml: 0.5}} onClick={handleEditRoleDialogOpen}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                }
                            </MDTypography>
                            {
                                authorization.delete && <MDDeleteButton onDelete={handleDelete}
                                             disabled={!authorization.delete}
                                             dialogTitle={t('user:deleteDialog.title')}
                                             dialogMessage={t('user:deleteDialog.message')}
                                             variant="text"
                                             size="small"/>
                            }
                        </MDBox>
                    </Grid>
                    <Grid item xs={12} md={5} lg={4} sx={{ ml: "auto" }}>
                        {tabsEl}
                    </Grid>
                </Grid>
                {children}
            </Card>
            {
                editRoleDialogOpen && <EditRoleDialog user={user} close={handleEditRoleDialogClose}/>
            }
        </MDBox>
    );
}

// Setting default props for the Header
Header.defaultProps = {
    children: "",
    tabsEl: null,
};

// Typechecking props for the Header
Header.propTypes = {
    children: PropTypes.node,
};

export default Header;
